/*
 * Copyright (c) 2022. Lukas Tschaftary
 *
 * NOTICE:  All information contained herein is, and remains the property of Lukas Tschaftary and his suppliers, if any.
 * The intellectual and technical concepts contained herein are proprietary to Lukas Tschaftary and its suppliers and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained from Lukas Tschaftary.
 */
import React from "react";
import objectPath from "object-path";

/**
 * This component allows to spawn drawers and modals
 * @type {{drawer: (function(*=, *=, *=, *=): {setState: setState, close: close, open: open}), modal: (function(*=, *=, *=, *=): {setState: setState, close: close, open: open})}}
 */
const Util = function () {


    /**
     *    This set of functions allows to work with arrays
     *    @type {{getElementByJsonKey: (function(Object[], string, (string|number|boolean)): Object)}}
     */
    const arrays = function () {

        /**
         * This function returns a element af an array by one of its key value peers
         * @param {object[]} arrayItem - The array in which to search for
         * @param {string} keyName - The name of key in the json
         * @param {string | number | boolean} keyValue - The value
         * @return {object}
         */
        function getElementByJsonKey(arrayItem, keyName, keyValue) {
            return arrayItem[arrayItem.findIndex(item => item[keyName] === keyValue)]

        }

        // return the functions
        return {

            getElementByJsonKey: getElementByJsonKey,
        }

    }()
    const number = function () {


        function round(number, decimal = 0) {
            return Number(number.toFixed(decimal));

        }

        // return the functions
        return {

            round: round,
        }

    }()

    /**
     *    This set of functions allows to work with arrays
     *    @type {{getElementByJsonKey: (function(Object[], string, (string|number|boolean)): Object)}}
     */
    const objects = function () {


        function areEqual(a, b) {
            if (a === b) return true;

            if (a && b && typeof a == 'object' && typeof b == 'object') {
                if (a.constructor !== b.constructor) return false;

                var length, i, keys;
                if (Array.isArray(a)) {
                    length = a.length;
                    if (length != b.length) return false;
                    for (i = length; i-- !== 0;)
                        if (!areEqual(a[i], b[i])) return false;
                    return true;
                }

                if ((a instanceof Map) && (b instanceof Map)) {
                    if (a.size !== b.size) return false;
                    for (i of a.entries())
                        if (!b.has(i[0])) return false;
                    for (i of a.entries())
                        if (!areEqual(i[1], b.get(i[0]))) return false;
                    return true;
                }

                if ((a instanceof Set) && (b instanceof Set)) {
                    if (a.size !== b.size) return false;
                    for (i of a.entries())
                        if (!b.has(i[0])) return false;
                    return true;
                }

                if (ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
                    length = a.length;
                    if (length != b.length) return false;
                    for (i = length; i-- !== 0;)
                        if (a[i] !== b[i]) return false;
                    return true;
                }


                if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
                if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
                if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();

                keys = Object.keys(a);
                length = keys.length;
                if (length !== Object.keys(b).length) return false;

                for (i = length; i-- !== 0;)
                    if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;

                for (i = length; i-- !== 0;) {
                    var key = keys[i];

                    if (key === '_owner' && a.$$typeof) {
                        // React-specific: avoid traversing React elements' _owner.
                        //  _owner contains circular references
                        // and is not needed when comparing the actual elements (and not their owners)
                        continue;
                    }
                    if (!areEqual(a[key], b[key])) return false;
                }

                return true;
            }

            // true if both NaN, false otherwise
            return a !== a && b !== b;
        };

        function flattenObject(ob) {

            // The object which contains the
            // final result
            let result = {};

            // loop through the object "ob"
            for (const i in ob) {

                // We check the type of the i using
                // typeof() function and recursively
                // call the function again
                if ((typeof ob[i]) === 'object' && !Array.isArray(ob[i])) {
                    const temp = flattenObject(ob[i]);
                    for (const j in temp) {

                        // Store temp in result
                        result[i + '.' + j] = temp[j];
                    }
                } else if (Array.isArray(ob[i])) {
                    ob[i].map((arrayObj, index) => {
                        const temp = flattenObject(arrayObj);
                        for (const j in temp) {

                            // Store temp in result
                            result[i + '.' + index + '.' + j] = temp[j];
                        }
                    })

                }

                // Else store ob[i] in result directly
                else {
                    result[i] = ob[i];
                }
            }
            return result;
        }


        // return the functions
        return {

            areEqual: areEqual, flattenObject: flattenObject
        }

    }()
    /**
     *    This set of functions allows to work with tree
     *    @type {{recursiveSearch: (function(*, {key: string}, *): [number,number]|[number]|*), getTreeFromFlatData: ((function({flatData: !Object[], getKey?: !Function, getParentKey?: !Function, rootKey?: (string|number)}): Object[])|*)}}
     */
    const tree = function () {

        /**
         * Generate a tree structure from flat data.
         * @link https://github.com/frontend-collective/react-sortable-tree/blob/master/src/utils/tree-data-utils.js
         * @param {!Object[]} flatData
         * @param {!function=} getKey - Function to get the key from the nodeData
         * @param {!function=} getParentKey - Function to get the parent key from the nodeData
         * @param {string|number=} rootKey - The value returned by `getParentKey` that corresponds to the root node.
         *                                  For example, if your nodes have id 1-99, you might use rootKey = 0
         *
         * @return {Object[]} treeData - The flat data represented as a tree
         */
        function getTreeFromFlatData({
                                         flatData,
                                         getKey = node => node.id,
                                         getParentKey = node => node.parentId,
                                         rootKey = '0',
                                     }) {
            if (!flatData) {
                return [];
            }

            const childrenToParents = {};
            flatData.forEach(child => {
                const parentKey = getParentKey(child);

                if (parentKey in childrenToParents) {
                    childrenToParents[parentKey].push(child);
                } else {
                    childrenToParents[parentKey] = [child];
                }
            });

            if (!(rootKey in childrenToParents)) {
                return [];
            }

            const trav = parent => {
                const parentKey = getKey(parent);
                if (parentKey in childrenToParents) {
                    return {
                        ...parent,
                        children: childrenToParents[parentKey].map(child => trav(child)),
                    };
                }

                return {...parent};
            };

            return childrenToParents[rootKey].map(child => trav(child));
        }

        /**
         * This function searches in a tree for a specific value
         *
         * @param  treeObject - The tree
         * @param {{key: string}} searchFor - The key and value to search for
         * @param childKeyName - The name of the children in the tree
         * @return {number[]}
         */
        function recursiveSearch(treeObject, searchFor, childKeyName) {


            function recursivePart(treeObject, searchForKey, searchForValue, childKeyName) {
                let index = 0;
                for (const key in treeObject) {
                    const value = treeObject[key];

                    if (Array.isArray(value)) {

                        const array = value;
                        for (let i = 0; i < array.length; i++) {

                            if (array[i][searchForKey] === searchForValue.toString()) {
                                return [index, i];
                            }
                        }
                    } else if (typeof treeObject === "object") {

                        if ((value[searchForKey] || "").toString() === searchForValue.toString()) {
                            return [index];
                        }
                        const result = recursivePart(value[childKeyName], searchForKey, searchForValue, childKeyName);
                        if (Array.isArray(result)) {
                            return [index].concat(result);
                        }
                    }

                    index++;
                }
            }

            let searchForKey = Object.keys(searchFor)[0]
            let searchForValue = Object.values(searchFor)[0]

            return recursivePart(treeObject, searchForKey, searchForValue, childKeyName)


        }

        // return the functions
        return {

            getTreeFromFlatData: getTreeFromFlatData, recursiveSearch: recursiveSearch
        }

    }()
    /**
     *    This set of functions allows to work with tree
     *    @type {{debounce: debounce, throttle: throttle}}
     */
    const performance = function () {
        const nativeMax = Math.max;
        const nativeMin = Math.min;

        function debounce(func, wait, options) {
            let lastArgs,
                lastThis,
                maxWait,
                result,
                timerId,
                lastCallTime,
                lastInvokeTime = 0,
                leading = false,
                maxing = false,
                trailing = true;
            if (typeof func !== 'function') {
                throw new TypeError("The first input has to be a function.");
            }
            wait = Number(wait) || 0;
            if (typeof options === 'object') {
                leading = !!options.leading;
                maxing = 'maxWait' in options;
                maxWait = maxing
                    ? nativeMax(Number(options.maxWait) || 0, wait)
                    : maxWait;
                trailing = 'trailing' in options
                    ? !!options.trailing
                    : trailing;
            }

            function invokeFunc(time) {
                let args = lastArgs,
                    thisArg = lastThis;

                lastArgs = lastThis = undefined;
                lastInvokeTime = time;
                result = func.apply(thisArg, args);
                return result;
            }

            function leadingEdge(time) {
                // Reset any `maxWait` timer.
                lastInvokeTime = time;
                // Start the timer for the trailing edge.
                timerId = setTimeout(timerExpired, wait);
                // Invoke the leading edge.
                return leading
                    ? invokeFunc(time)
                    : result;
            }

            function remainingWait(time) {
                let timeSinceLastCall = time - lastCallTime,
                    timeSinceLastInvoke = time - lastInvokeTime,
                    result = wait - timeSinceLastCall;
                console.log('remainingWait');
                return maxing
                    ? nativeMin(result, maxWait - timeSinceLastInvoke)
                    : result;
            }

            function shouldInvoke(time) {
                let timeSinceLastCall = time - lastCallTime,
                    timeSinceLastInvoke = time - lastInvokeTime;
                // Either this is the first call, activity has stopped and we're at the trailing
                // edge, the system time has gone backwards and we're treating it as the
                // trailing edge, or we've hit the `maxWait` limit.
                return (lastCallTime === undefined || (timeSinceLastCall >= wait) || (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
            }

            function timerExpired() {
                const time = Date.now();
                if (shouldInvoke(time)) {
                    return trailingEdge(time);
                }
                // Restart the timer.
                timerId = setTimeout(timerExpired, remainingWait(time));
            }

            function trailingEdge(time) {
                timerId = undefined;

                // Only invoke if we have `lastArgs` which means `func` has been debounced at
                // least once.
                if (trailing && lastArgs) {
                    return invokeFunc(time);
                }
                lastArgs = lastThis = undefined;
                return result;
            }

            function cancel() {
                if (timerId !== undefined) {
                    clearTimeout(timerId);
                }
                lastInvokeTime = 0;
                lastArgs = lastCallTime = lastThis = timerId = undefined;
            }

            function flush() {
                return timerId === undefined
                    ? result
                    : trailingEdge(Date.now());
            }

            function debounced() {
                let time = Date.now(),
                    isInvoking = shouldInvoke(time);
                lastArgs = arguments;
                lastThis = this;
                lastCallTime = time;

                if (isInvoking) {
                    if (timerId === undefined) {
                        return leadingEdge(lastCallTime);
                    }
                    if (maxing) {
                        // Handle invocations in a tight loop.
                        timerId = setTimeout(timerExpired, wait);
                        return invokeFunc(lastCallTime);
                    }
                }
                if (timerId === undefined) {
                    timerId = setTimeout(timerExpired, wait);
                }
                return result;
            }

            debounced.cancel = cancel;
            debounced.flush = flush;
            return debounced;
        }


        function throttle(func, wait, options) {
            let leading = true,
                trailing = true;

            if (typeof func !== 'function') {
                throw new TypeError("The first input has to be a function.");
            }
            if (typeof options === 'object') {
                leading = 'leading' in options
                    ? !!options.leading
                    : leading;
                trailing = 'trailing' in options
                    ? !!options.trailing
                    : trailing;
            }
            return debounce(func, wait, {
                leading,
                maxWait: wait,
                trailing,
            });
        }


        // return the functions
        return {

            debounce: debounce, throttle: throttle
        }

    }()
    /**
     *    This set of functions allows to work with DOM nodes
     *    @param {Node} domNode
     *
     */
    const domNode = function (domNode) {

        /**
         * This function gives back the node and its childes as a JSON
         */
        function getAsJSON() {

            const Element = e => ({
                toJSON: () => ({
                    tagName:
                    e.tagName,
                    textContent:
                    e.textContent,
                    attributes:
                        Array.from(e.attributes, ({
                                                      name,
                                                      value
                                                  }) => ({[name]: value})).reduce(function (result, current) {
                            return Object.assign(result, current);
                        }, {}),
                    children:
                        Array.from(e.children, Element)
                })
            })

            return JSON.parse(JSON.stringify(Element(domNode), null, '  '))
        }

        // return the functions
        return {
            getAsJSON: getAsJSON
        }

    }

    /**
     *    This set of functions allows to work with colors
     *    @type {{rgbaToHex: (function(*): string), getTextColorForBackground: (function(string): string), applyColorOpacity: (function(*, *): string)}}
     */
    const color = function () {

        /**
         * This function returns the color of the text
         * @param {string} hexColor - The hex value of the background color
         * @link https://gomakethings.com/dynamically-changing-the-text-color-based-on-background-color-contrast-with-vanilla-js/
         * @return { 'black' | 'white'}
         */
        function getTextColorForBackground(hexColor) {

            // If a leading # is provided, remove it
            if (hexColor.slice(0, 1) === '#') {
                hexColor = hexColor.slice(1);
            }

            // If a three-character hexcode, make six-character
            if (hexColor.length === 3) {
                hexColor = hexColor.split('').map(function (hex) {
                    return hex + hex;
                }).join('');
            }

            // Convert to RGB value
            let r = parseInt(hexColor.substr(0, 2), 16);
            let g = parseInt(hexColor.substr(2, 2), 16);
            let b = parseInt(hexColor.substr(4, 2), 16);

            // Get YIQ ratio
            let yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
            // Check contrast
            return (yiq >= 128) ? 'black' : 'white';
        }

        /**
         * This function return returns a new color based on the input and the target opacity
         * @param hexCode
         * @param opacity
         * @return {string}
         */
        function applyColorOpacity(hexCode, opacity) {
            let hex = hexCode.replace('#', '');

            if (hex.length === 3) {
                hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
            }

            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);

            return `rgba(${r},${g},${b},${opacity / 100})`;
        }

        /**
         * This functions converts rgba colors to hex
         * @param rgba
         * @return {string|string}
         */
        function rgbaToHex(rgba) {
            let sep = rgba.indexOf(",") > -1 ? "," : " ";
            rgba = rgba.substr(5).split(")")[0].split(sep);

            // Strip the slash if using space-separated syntax
            if (rgba.indexOf("/") > -1)
                rgba.splice(3, 1);

            for (let R in rgba) {
                let r = rgba[R];
                if (r.indexOf("%") > -1) {
                    let p = r.substr(0, r.length - 1) / 100;

                    if (R < 3) {
                        rgba[R] = Math.round(p * 255);
                    } else {
                        rgba[R] = p;
                    }
                }
            }
            let r = (+rgba[0]).toString(16),
                g = (+rgba[1]).toString(16),
                b = (+rgba[2]).toString(16),
                a = Math.round(+rgba[3] * 255).toString(16);

            if (r.length == 1)
                r = "0" + r;
            if (g.length == 1)
                g = "0" + g;
            if (b.length == 1)
                b = "0" + b;
            if (a.length == 1)
                a = "0" + a;

            return "#" + r + g + b + a;
        }

        // return the functions
        return {

            getTextColorForBackground: getTextColorForBackground,
            applyColorOpacity: applyColorOpacity,
            rgbaToHex: rgbaToHex
        }

    }()
    /**
     *    This set of functions allows to work with colors
     *    @type {{levenshteinDistance: (function(string=, string=): *)}}
     */
    const algo = function () {

        /**
         * This function uses the Levenshtein distance algorithm to get a score for the similarity of two stings source  https://www.tutorialspoint.com/levenshtein-distance-in-javascript
         * @return A integer 0 is an exact match than the numbers go up
         * @link https://en.wikipedia.org/wiki/Levenshtein_distance
         * @param strOne {string}
         * @param strTwo {string}
         * @returns number
         */
        function levenshteinDistance(strOne = '', strTwo = '') {
            const track = Array(strTwo.length + 1).fill(null).map(() =>
                Array(strOne.length + 1).fill(null));
            for (let i = 0; i <= strOne.length; i += 1) {
                track[0][i] = i;
            }
            for (let j = 0; j <= strTwo.length; j += 1) {
                track[j][0] = j;
            }
            for (let j = 1; j <= strTwo.length; j += 1) {
                for (let i = 1; i <= strOne.length; i += 1) {
                    const indicator = strOne[i - 1] === strTwo[j - 1] ? 0 : 1;
                    track[j][i] = Math.min(
                        track[j][i - 1] + 1, // deletion
                        track[j - 1][i] + 1, // insertion
                        track[j - 1][i - 1] + indicator, // substitution
                    );
                }
            }
            return track[strTwo.length][strOne.length];
        }

        // return the functions
        return {
            levenshteinDistance: levenshteinDistance,
        }

    }()

    /**
     *    This set of functions allows to work with colors
     *    @type {{get: (function(object= test, string=): *),coalesce: (function(object= test, string=): *),empty: (function(object= test, string=): *),set: (function(object= test, string=): *),insert: (function(object= test, string=): *),push: (function(object= test, string=): *),ensureExists: (function(object= test, string=): *),del: (function(object= test, string=): *)}}
     */
    const objectPaths = function () {
        return objectPath


    }()
    const path = function () {
        function join(...input) {
            return input.join('/').replace(/\/{2,}/, '/')
        }

        // return the functions
        return {
            join: join,
        }

    }()

    function log(variable) {
        new Error("Not correctly  implemented")

        console.log(Object.keys({variable})[0] + ":" + variable)
    }

    const time = function () {
        function sleep(ms) {
            return new Promise((resolve) => {
                setTimeout(resolve, ms);
            });
        }

        return {sleep: sleep}
    }()


    const files = function () {
        /**
         *
         * @param fileName
         * @param fileData
         */
        function fileDownloadInBrowser(fileName, fileData) {
            // // download the files
            let url = "data:application/pdf;base64," + fileData;
            let a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
            a.click();
            a.remove();  //afterwards we remove the element again
        }

        return {fileDownloadInBrowser: fileDownloadInBrowser}
    }()

    return {
        arrays: arrays,
        tree: tree,
        performance: performance,
        log: log,
        domNode: domNode,
        path: path,
        color: color,
        algo: algo,
        objects: objects,
        objectPaths: objectPaths,
        time: time,
        files: files,
        number: number,
    }
}()


export {
    Util,
};